/** Revenge (Abyssal Coil): the more it has bled, the harder it hits.
 *
 * The expansion's own formula, as a multiplier on damage before the attack:
 *
 *     bonus = sqrt( (start + 1) * hp / (healthNow + hp) - 1 )
 *
 * where `start` is the stack's count at the beginning of the battle, `hp` is
 * one creature's health, and `healthNow` is what the stack has left. At full
 * strength the ratio is exactly 1 and the bonus is exactly 0; the last wounded
 * Abyssal Coil of forty hits for several times its listed damage.
 *
 * **This is the ability that needed `StackStore.startCount`** - the column that
 * was scoped and then cancelled earlier the same day because nothing consumed
 * it. Something does now, and it still does not need a column: the starting
 * count is written once, at battle start, into a marker modifier on the stack,
 * and only for creatures that have the ability. Same shape as `runes.ts` and
 * `devour.ts`. The save format is untouched, the journal covers it, and stacks
 * without Revenge carry nothing at all.
 *
 * Integer throughout, in `FP_ONE` fixed point, with a Newton integer square
 * root - `Math.sqrt` returns a float and D2 wants the same bytes on every
 * machine.
 *
 * This file imports nothing from `abilities.ts` on purpose. `damage.ts` needs
 * `revengeBonusFp`, and `abilities.ts` already imports `damage.ts`, so reaching
 * for `abilitiesOf` here would close damage -> revenge -> abilities -> damage.
 * The ability check belongs where the def is already in hand - `init.ts` walks
 * `def.abilities` at battle start - and everything after that reads the note
 * off the stack instead.
 */

import type { Modifier, StatKey } from "../modifiers.js";
import { asModifierSrcId } from "../ids.js";
import { FP_ONE } from "../numeric.js";
import type { BattleState } from "./state.js";

/**
 * Carries the battle-start count in its `value`.
 *
 * Every stack gets one, not only the Revenge creatures it was written for:
 * Resurrection and Raise Fallen need the same number as their ceiling, or a
 * cast on an unwounded stack grows it past what marched in.
 */
export const SRC_START_COUNT = asModifierSrcId("battle.start_count");
/** @deprecated the marker is `SRC_START_COUNT`; kept so old readers compile. */
export const SRC_REVENGE_BASE = SRC_START_COUNT;
/**
 * The stat the note is filed under.
 *
 * A real minted key, not `"attack"` with a smuggled extra field: `Modifier` is
 * a persisted shape with an exhaustive key list, so an undeclared `startCount`
 * property would fail `findShapeIssues` and quietly not survive a save. Filing
 * it under a key nothing resolves keeps the count out of every stat sum while
 * staying inside the shape.
 */
export const STAT_START_COUNT = "battle.start_count" as StatKey;
/** @deprecated see `STAT_START_COUNT`. */
export const STAT_REVENGE_BASE = STAT_START_COUNT;

/** The modifier that remembers what the stack started with. */
export function startCountModifier(startCount: number): Modifier {
  return {
    source: SRC_START_COUNT, stat: STAT_START_COUNT, op: "max",
    value: startCount, expires: { kind: "battle" },
  };
}
/** @deprecated see `startCountModifier`. */
export const revengeBaseModifier = startCountModifier;

/**
 * What the stack began the battle with, or 0 for a stack that carries no
 * marker - a battle saved before every stack was stamped.
 */
export function startCount(s: BattleState, slot: number): number {
  for (const m of s.stacks.mods[slot] ?? []) {
    if (m.source === SRC_START_COUNT) return m.value;
  }
  return 0;
}
/** @deprecated see `startCount`. */
export const revengeBase = startCount;

/** Integer square root, Newton's method. Deterministic across machines. */
export function isqrt(n: number): number {
  if (n <= 0) return 0;
  let x = n;
  let y = Math.trunc((x + 1) / 2);
  while (y < x) {
    x = y;
    y = Math.trunc((x + Math.trunc(n / x)) / 2);
  }
  return x;
}

/**
 * The fixed-point damage bonus Revenge is worth right now, or 0.
 *
 * Returns a multiplier delta: `FP_ONE / 2` means "half again". `computeDamage`
 * adds it into the same additive bracket the attack-versus-defence difference
 * feeds, which is where the expansion puts it - "an increase to the Damage parameter
 * before the attack".
 */
export function revengeBonusFp(s: BattleState, slot: number, maxHp: number): number {
  const start = revengeBase(s, slot);
  if (start <= 0 || maxHp <= 0) return 0;
  const st = s.stacks;
  const count = st.count[slot]!;
  if (count <= 0) return 0;
  const healthNow = (count - 1) * maxHp + st.topHp[slot]!;
  const denom = healthNow + maxHp;
  if (denom <= 0) return 0;
  // ratio in FP: ((start + 1) * hp) / (healthNow + hp)
  const ratioFp = Math.trunc(((start + 1) * maxHp * FP_ONE) / denom);
  const excessFp = ratioFp - FP_ONE;
  if (excessFp <= 0) return 0;
  // sqrt of a fixed-point number: sqrt(x/FP) * FP === sqrt(x * FP).
  return isqrt(excessFp * FP_ONE);
}
